import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Wrench, Loader2 } from 'lucide-react';
import { apiEndpoints, apiRequest } from '@/config/api';
import { AboutContent, ApiResponse } from '@/types/api';

const AboutSection = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['about'],
    queryFn: () => apiRequest<ApiResponse<AboutContent>>(apiEndpoints.about),
  });

  const aboutContent = data?.data;

  const features = [{
    icon: Award,
    title: 'کیفیت برتر',
    description: 'استفاده از بهترین مواد اولیه و تکنولوژی روز'
  }, {
    icon: Users,
    title: 'تیم متخصص',
    description: 'نیروی انسانی مجرب و متخصص در زمینه الکترو موتورهای الکتریکی'
  }, {
    icon: Wrench,
    title: 'خدمات پس از فروش',
    description: 'پشتیبانی کامل و خدمات نگهداری و تعمیرات'
  }];

  if (isLoading) {
    return (
      <section id="about" className="py-20 bg-background">
        <div className="container mx-auto px-4 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-copper" />
        </div>
      </section>
    );
  }

  const content = aboutContent?.content || 'شرکت الکترو موتورسازی الکتریک با بیش از 20 سال تجربه در زمینه طراحی و تولید الکترو موتورهای الکتریکی، یکی از پیشگامان صنعت الکترو موتورسازی در کشور محسوب می‌شود. ما با تکیه بر دانش فنی روز دنیا و استفاده از تجهیزات مدرن، محصولاتی با کیفیت بین‌المللی تولید می‌کنیم.';

  return (
    <section id="about" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image Side */}
          <motion.div 
            initial={{ opacity: 0, x: 0 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8 }} 
            viewport={{ once: true }} 
            className="relative"
          >
            <div className="relative rounded-lg overflow-hidden shadow-2xl">
              <img src="/images/hero-factory-real-v2.webp" alt="محیط کارخانه و تولید الکتروموتورهای کیا" width={1672} height={900} loading="lazy" decoding="async" className="w-full h-auto object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-steel-dark/20 to-transparent"></div>
            </div>
            
            {/* Floating Stats */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }} 
              whileInView={{ opacity: 1, scale: 1 }} 
              transition={{ duration: 0.8, delay: 0.3 }} 
              viewport={{ once: true }} 
              className="absolute -bottom-6 -right-6 bg-copper text-white p-6 rounded-lg shadow-lg"
            >
              <div className="text-center">
                <div className="text-2xl font-bold">ISO 9001</div>
                <div className="text-sm">گواهی کیفیت</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <motion.div 
            initial={{ opacity: 0, x: 0 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8 }} 
            viewport={{ once: true }} 
            className="space-y-6"
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                {aboutContent?.title || 'درباره شرکت ما'}
              </h2>
              <p className="text-steel dark:text-slate-300 text-lg leading-relaxed font-medium text-justify">
                {content}
              </p>
            </div>

            <div className="space-y-4">
              {features.map((feature, index) => (
                <motion.div 
                  key={feature.title} 
                  initial={{ opacity: 0, y: 20 }} 
                  whileInView={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }} 
                  viewport={{ once: true }} 
                  className="flex items-start space-x-4 space-x-reverse"
                >
                  <div className="flex-shrink-0 p-2 bg-copper/10 rounded-lg">
                    <feature.icon className="w-6 h-6 text-copper" />
                  </div>
                  <div className="flex flex-col justify-start">
                    <h3 className="font-semibold text-steel-dark dark:text-white mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-steel-dark dark:text-slate-300 text-sm font-medium">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div 
              initial={{ opacity: 0 }} 
              whileInView={{ opacity: 1 }} 
              transition={{ duration: 0.8, delay: 0.6 }} 
              viewport={{ once: true }} 
              className="pt-6"
            >
              <div className="flex items-center space-x-2 space-x-reverse text-copper">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">مجوز رسمی از وزارت صنعت</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
